import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "@/api/client";
import { errorMessage, fieldErrors } from "@/api/errors";

export default function InviteAcceptPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setFieldErr({});
    if (password !== confirm) {
      setFieldErr({ confirm: "两次输入的密码不一致" });
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch("/invitations/accept", {
        method: "POST",
        body: JSON.stringify({ token, password, displayName: displayName || null }),
      });
      const data = await r.json();
      if (!r.ok) {
        setErr(errorMessage(data));
        setFieldErr(fieldErrors(data));
        return;
      }
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e) {
      setErr(errorMessage(e, "网络异常"));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="bg-background rounded-lg border border-border p-8 max-w-md">
          <h1 className="text-lg font-semibold mb-2">链接无效</h1>
          <p className="text-sm text-muted-foreground">
            缺少邀请 token。请从邀请邮件中重新打开链接。
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="bg-background rounded-lg border border-border p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold mb-2">注册成功</h1>
          <p className="text-sm text-muted-foreground">即将跳转到登录页...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <form
        onSubmit={onSubmit}
        className="bg-background w-full max-w-md rounded-lg border border-border p-8 shadow-sm space-y-5"
      >
        <div>
          <h1 className="text-xl font-semibold">接受邀请</h1>
          <p className="text-muted-foreground text-sm mt-1">
            设置你的登录密码完成注册
          </p>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">显示名（选填）</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="你的姓名"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">密码</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="≥10 位，含大小写字母和数字"
          />
          {fieldErr.password && (
            <span className="text-xs text-destructive">{fieldErr.password}</span>
          )}
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">确认密码</span>
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {fieldErr.confirm && (
            <span className="text-xs text-destructive">{fieldErr.confirm}</span>
          )}
        </label>

        {err && (
          <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded-md p-2">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "提交中..." : "完成注册"}
        </button>
      </form>
    </div>
  );
}
